import { createApiClient, type ApiClient } from "../api";
import { createCadesPluginAdapter, type CryptoProAdapter } from "../cryptopro";
import { IdentityApi } from "../auth";
import { parseEnvironment } from "../config/environment";
import { AdministrationApi } from "../entities/access";
import { ModulesApi } from "../entities/modules";
import { OperationsApi } from "../entities/operations";
import { CounterpartiesApi } from "../entities/counterparties";
import { DocumentWorkflowApi } from "../entities/document-workflow";
import { ApprovalsApi } from "../entities/document-workflow/approvals";
import { SubstitutionsApi } from "../entities/document-workflow/substitutions";
import { FilesApi } from "../entities/files";
import { MailboxesApi } from "../entities/mailboxes";
import { AuditApi } from "../entities/audit";
import { NotificationsApi } from "../entities/notifications";
import { SiteContentApi } from "../entities/site-content";
import { UserGuideApi } from "../entities/user-guide";

export interface ShellServices {
  substitutions?: Pick<
    SubstitutionsApi,
    "list" | "candidates" | "create" | "cancel"
  >;
  approvals?: Pick<
    ApprovalsApi,
    | "templates"
    | "templateCandidates"
    | "template"
    | "createTemplate"
    | "updateTemplate"
    | "documentApprovals"
    | "candidates"
    | "approval"
    | "start"
    | "decide"
    | "resubmit"
    | "withdraw"
    | "tasks"
  >;
  apiClient?: ApiClient;
  notifications?: Pick<
    NotificationsApi,
    | "list"
    | "unreadCount"
    | "markRead"
    | "markAllRead"
    | "preferences"
    | "updatePreferences"
    | "realtimeConfig"
    | "registerPush"
    | "unregisterPush"
    | "pushSubscriptions"
  >;
  identity: Pick<
    IdentityApi,
    | "challengeTwoFactor"
    | "currentUser"
    | "updateProfile"
    | "assignAvatar"
    | "removeAvatar"
    | "requestEmailChange"
    | "login"
    | "logout"
    | "forgotPassword"
    | "resetPassword"
    | "changePassword"
    | "confirmPassword"
    | "sendEmailVerification"
    | "setupTwoFactor"
    | "twoFactorQrCode"
    | "verifyEmail"
    | "confirmTwoFactor"
    | "recoveryCodes"
    | "regenerateRecoveryCodes"
    | "disableTwoFactor"
    | "sessions"
    | "destroySession"
    | "destroyOtherSessions"
  >;
  modules: Pick<ModulesApi, "navigation"> &
    Partial<
      Pick<
        ModulesApi,
        "list" | "enable" | "disable" | "settings" | "updateSettings"
      >
    >;
  files: Pick<
    FilesApi,
    "initiate" | "uploadObject" | "confirm" | "download" | "preview"
  > &
    Partial<
      Pick<
        FilesApi,
        | "list"
        | "delete"
        | "rename"
        | "get"
        | "folders"
        | "createFolder"
        | "updateFolder"
        | "folderScopes"
        | "replaceFolderScopes"
        | "move"
      >
    >;
  /** Браузерный плагин КриптоПро; в тестах подставляется поддельный. */
  cryptoPro?: CryptoProAdapter;
  operations?: Pick<
    OperationsApi,
    | "build"
    | "readiness"
    | "queueHealth"
    | "outboxHealth"
    // Проверка доставки: человек нажимает кнопку и смотрит, дошло ли
    // уведомление до центра и до браузера.
    | "testNotification"
  >;
  administration: Pick<
    AdministrationApi,
    | "selectScope"
    | "selectAllScopes"
    | "listScopes"
    | "organizationTree"
    | "organizationUnit"
    | "hierarchy"
    | "hierarchyReach"
    | "setManager"
    | "createScope"
    | "updateScope"
    | "deleteScope"
    | "listDepartments"
    | "createDepartment"
    | "updateDepartment"
    | "deleteDepartment"
    | "listMemberships"
    | "saveMembership"
    | "deleteMembership"
    | "listUsers"
    | "user"
    | "createUser"
    | "inviteUser"
    | "updateUser"
    | "transitionUser"
    | "setUserStatus"
    | "history"
    | "sessions"
    | "endSession"
    | "assignAvatar"
    | "removeAvatar"
    | "listRoles"
    | "assignRole"
    | "revokeRole"
    | "overrides"
    | "replaceOverrides"
    | "requireTwoFactor"
  >;
  access?: Pick<
    AdministrationApi,
    | "listRoles"
    | "role"
    | "createRole"
    | "updateRole"
    | "deleteRole"
    | "replaceRolePermissions"
    | "assignRole"
    | "revokeRole"
    | "overrides"
    | "replaceOverrides"
    | "permissions"
    | "effectivePermissions"
    | "listUsers"
    | "listScopes"
    | "criticalAdministrators"
    | "previewTransfer"
    | "commitTransfer"
  >;
  documentWorkflow: Pick<
    DocumentWorkflowApi,
    | "categories"
    | "counterparties"
    | "createDocument"
    | "updateDocument"
    | "register"
    | "assign"
    | "setControl"
    | "lifecycle"
    | "assignments"
    | "reads"
    | "relations"
    | "timeline"
    | "scopes"
    | "shareScope"
    | "createRelation"
    | "requestHistoryExport"
    | "participants"
    | "participantCandidates"
    | "replaceRecipients"
    | "addParticipant"
    | "removeParticipant"
    | "removeAssignment"
    | "removeControl"
    | "revokeScope"
    | "deleteRelation"
    | "historyExportStatus"
    | "historyExportDownload"
    | "comments"
    | "addComment"
    | "updateComment"
    | "deleteComment"
    | "readDiscussion"
    | "timelinePage"
    | "compose"
    | "document"
    | "folders"
    | "attachments"
    | "download"
    | "documents"
    | "documentFiles"
    | "attachFile"
    | "deleteDocumentFile"
    | "renameDocumentFile"
    | "downloadVersion"
    | "documentFile"
    | "fileVersions"
    | "createFileVersion"
    | "signatureCapabilities"
    | "createSignatureRequest"
    | "completeSignatureRequest"
    | "versionSignatures"
    | "verifySignature"
    | "signatureFile"
    | "requestSignatureStamp"
    | "signatureStamp"
    | "downloadSignatureStamp"
    | "downloadMailAttachment"
    | "linkMailAttachment"
    | "linkFile"
    | "previewMailAttachment"
    | "unlinkDocumentVersion"
    | "previewVersion"
    | "dashboard"
    | "deadlineReport"
    | "executorReport"
    | "listDocuments"
    | "markRead"
    | "message"
    | "messages"
    | "threads"
    | "outbox"
    | "reply"
    | "markMessageRead"
    | "markThreadRead"
    | "retryOutbox"
    | "cancelOutbox"
    | "linkMessage"
    | "unlinkMessage"
    | "mergeThread"
    | "splitThread"
  >;
  /** Справочник контрагентов и разбор отправителей; в тестах подставляется поддельный. */
  counterparties?: Pick<
    CounterpartiesApi,
    | "list"
    | "get"
    | "create"
    | "update"
    | "addContact"
    | "deleteContact"
    | "rules"
    | "createRule"
    | "deleteRule"
    | "unresolvedMessages"
    | "candidates"
    | "resolve"
  >;
  /** Почтовые ящики и ошибки импорта; в тестах подставляется поддельный. */
  mailboxes?: Pick<
    MailboxesApi,
    | "list"
    | "get"
    | "create"
    | "update"
    | "testConnection"
    | "sync"
    | "syncState"
    | "failures"
    | "failure"
    | "retryFailure"
    | "resolveFailure"
  >;
  siteContent?: Pick<
    SiteContentApi,
    | "listNews"
    | "news"
    | "previewNews"
    | "createNews"
    | "updateNews"
    | "publishNews"
    | "scheduleNews"
    | "unpublishNews"
    | "deleteNews"
    | "listCategories"
    | "categories"
    | "createCategory"
    | "updateCategory"
    | "deleteCategory"
    | "listPages"
    | "pages"
    | "page"
    | "createPage"
    | "updatePage"
    | "deletePage"
    | "settings"
    | "updateSettings"
    | "invalidateCache"
    | "createMedia"
    | "uploadObject"
    | "confirmMedia"
    | "uploadMedia"
    | "deleteMedia"
  >;
  audit: Pick<
    AuditApi,
    "list" | "event" | "exports" | "requestExport" | "exportStatus" | "download"
  >;
  /** Инструкция сотрудника: копия страницы Confluence; в тестах подставляется поддельная. */
  userGuide?: Pick<UserGuideApi, "get" | "refresh">;
}

export function createShellServices(): ShellServices {
  const environment = parseEnvironment(import.meta.env);
  const client = createApiClient({ baseUrl: environment.VITE_API_BASE_URL });

  return {
    apiClient: client,
    notifications: new NotificationsApi(client),
    identity: new IdentityApi(client),
    modules: new ModulesApi(client),
    files: new FilesApi(client, environment.VITE_API_BASE_URL),
    cryptoPro: createCadesPluginAdapter({
      pluginUrl: environment.VITE_CRYPTOPRO_PLUGIN_URL,
      timestampUrl: environment.VITE_CRYPTOPRO_TSA_URL,
    }),
    operations: new OperationsApi(client),
    administration: new AdministrationApi(client),
    access: new AdministrationApi(client),
    documentWorkflow: new DocumentWorkflowApi(client),
    approvals: new ApprovalsApi(client),
    substitutions: new SubstitutionsApi(client),
    counterparties: new CounterpartiesApi(client),
    mailboxes: new MailboxesApi(client),
    siteContent: new SiteContentApi(client),
    audit: new AuditApi(client),
    userGuide: new UserGuideApi(client),
  };
}
